import { supabase } from '../supabase';
import { resolveEdicionFromRouteParam } from '../../database/database';
import { parsePropuestaTrofeos } from '../utils/propuestaGanador';
import { mergeBypassRangoRestauranteEnJoin } from '../utils/restauranteBypassVotacion';

/** Campos de restaurante en joins usados en votación y listados de propuestas. */
const RESTAURANTE_JOIN_SELECT = `
  id,
  nombre_oficial,
  nombre_mostrar,
  breve_resena,
  sedes,
  imagen_principal,
  ciudad_id,
  latitud_sede_principal,
  longitud_sede_principal,
  ubicacion_gps,
  direccion,
  instagram,
  bypass_ubicacion,
  bypass_rango_restaurante,
  ciudades(id, nombre)
`;

function keysCamposModificadosHistorial(raw: unknown): string[] {
  if (raw == null) return [];
  try {
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return Object.keys(raw as Record<string, unknown>);
    }
    if (typeof raw === 'string') {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        return Object.keys(parsed);
      }
    }
  } catch {
    /* ignore */
  }
  return [];
}

/** Texto del título o workflow `estado_titulo` → "Estado título" en historial. */
function filaHistorialIncluyeCambioTituloContenido(keys: string[]): boolean {
  return keys.some((k) => {
    const t = k.trim();
    if (t === 'Título' || t.toLowerCase() === 'titulo') return true;
    const n = t.toLowerCase();
    return (
      t === 'Estado título' ||
      n === 'estado título' ||
      n === 'estado titulo' ||
      n === 'estado_titulo'
    );
  });
}

/** Texto de descripción o workflow `estado_descripcion` → "Estado descripción". */
function filaHistorialIncluyeCambioDescripcionContenido(keys: string[]): boolean {
  return keys.some((k) => {
    const t = k.trim();
    if (t === 'Descripción' || t.toLowerCase() === 'descripcion') return true;
    const n = t.toLowerCase();
    return (
      t === 'Estado descripción' ||
      n === 'estado descripción' ||
      n === 'estado descripcion' ||
      n === 'estado_descripcion'
    );
  });
}

/** Galería / foto seleccionada o workflow `estado_foto` → "Estado foto". */
function filaHistorialIncluyeCambioFoto(keys: string[]): boolean {
  return keys.some((k) => {
    const t = k.trim();
    if (t === 'Fotos' || t === 'Foto seleccionada') return true;
    const n = t.toLowerCase();
    return (
      t === 'Estado foto' ||
      n === 'estado foto' ||
      n === 'estado_foto' ||
      n === 'fotos' ||
      n === 'foto seleccionada' ||
      n === 'foto_seleccionada_index'
    );
  });
}

/** JSONB de PostgREST ya llega como objeto/array; no devolver defaultValue en ese caso. */
const parseJSONSafely = (value: unknown, defaultValue: any = []): any => {
  if (value == null || value === '') return defaultValue;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string' || value.trim() === '') return defaultValue;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('Error parseando JSON:', value, error);
    return defaultValue;
  }
};

/** Más reciente por edición o creación primero (desempate: created_at desc). */
function comparePropuestasPorReciente(
  a: { updated_at?: string | null; created_at?: string | null },
  b: { updated_at?: string | null; created_at?: string | null }
): number {
  const ta = new Date(a.updated_at || a.created_at || 0).getTime();
  const tb = new Date(b.updated_at || b.created_at || 0).getTime();
  if (tb !== ta) return tb - ta;
  const ca = new Date(a.created_at || 0).getTime();
  const cb = new Date(b.created_at || 0).getTime();
  return cb - ca;
}

const POSICION_NULL_SORT = 2_147_483_647;

function parseVentasPromedioEntero(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.trunc(n));
}

/** Orden de listado: `posicion` ascendente; sin valor va al final; desempate por reciente. */
export function comparePropuestasPorPosicionYLuegoReciente(a: Propuesta, b: Propuesta): number {
  const posA = a.posicion != null && !Number.isNaN(Number(a.posicion)) ? Number(a.posicion) : POSICION_NULL_SORT;
  const posB = b.posicion != null && !Number.isNaN(Number(b.posicion)) ? Number(b.posicion) : POSICION_NULL_SORT;
  if (posA !== posB) return posA - posB;
  return comparePropuestasPorReciente(a, b);
}

export interface Propuesta {
  id: string;
  edicion_id?: number;
  /** Orden manual en listados (menor = más arriba). */
  posicion?: number;
  /**
   * Trofeos oficiales (títulos de `ediciones.trofeos`). Array vacío = sin trofeo.
   */
  ganador?: string[] | null;
  restaurante_id: string;
  foto_restaurante?: string; // Mantener para compatibilidad
  /** `url` = original subido; `url_ficha` = recorte opcional para ficha. */
  fotos?: Array<{ url: string; id: string; url_ficha?: string }>;
  foto_seleccionada_index?: number;
  instagram?: string;
  titulo: string;
  descripcion?: string;
  /** Si true, la propuesta tiene nota adicional. */
  nota_activa?: boolean;
  nota_titulo?: string | null;
  nota_descripcion?: string | null;
  reel_participante_oficial?: string;
  reel_marcas_historias?: string;
  historias_participante_oficial?: string;
  estado_propuesta?: string;
  estado_publicacion?: string;
  estado_foto?: string;
  estado_titulo?: string;
  estado_descripcion?: string;
  visible_publico?: boolean;
  /** Ventas promedio (entero no negativo, opcional). */
  ventas_promedio?: number | null;
  /** Legacy: bypass de rango antes de moverlo a `restaurantes`. */
  bypass_rango_restaurante?: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  restaurante?: {
    id: string;
    nombre_oficial: string;
    nombre_mostrar: string;
    breve_resena?: string;
    imagen_principal?: string;
    ciudad_id?: string;
    sede_ubicacion_corta?: string;
    /** Columna BD `dirección` corta (misma info que sede_ubicacion_corta en capa app) */
    direccion?: string;
    ubicacion_gps?: string;
    latitud_sede_principal?: number | null;
    longitud_sede_principal?: number | null;
    /** Si true, cartel «Activa la ubicación» muestra Recargar con bypass (inferior). */
    bypass_ubicacion?: boolean;
    /** Si true, modal «fuera del local» permite bypass tocando el icono de alerta. */
    bypass_rango_restaurante?: boolean;
    ciudades?: {
      id: string;
      nombre: string;
    };
  };
  sedes?: PropuestaSede[];
  ultima_modificacion?: {
    usuario?: {
      id: string;
      nombre?: string;
      apellido?: string;
      email?: string;
    };
    campos_modificados?: string[];
    fecha_modificacion?: string;
  };
}

export interface PropuestaSede {
  id: string;
  propuesta_id: string;
  sede_index: number;
  sede_nombre: string;
  sede_direccion?: string;
  horarios_evento: any;
  ubicacion_gps?: string;
  /** Si false, la sede no se muestra en fichas públicas (edición, cdash). */
  visible_en_ficha?: boolean | null;
  created_at: string;
  updated_at: string;
}

/** Sedes que deben mostrarse en la ficha pública (`visible_en_ficha !== false`). */
export function filtrarSedesVisiblesEnFicha<T extends { visible_en_ficha?: boolean | null }>(
  sedes: T[] | undefined | null
): T[] {
  if (!sedes?.length) return [];
  return sedes.filter((s) => s.visible_en_ficha !== false);
}

export interface CreatePropuestaData {
  edicion_id?: number;
  restaurante_id: string;
  foto_restaurante?: string; // Mantener para compatibilidad
  fotos?: Array<{ url: string; id: string; url_ficha?: string }>;
  foto_seleccionada_index?: number;
  instagram?: string;
  titulo: string;
  descripcion?: string;
  nota_activa?: boolean;
  nota_titulo?: string | null;
  nota_descripcion?: string | null;
  visible_publico?: boolean;
  /** Ventas promedio (entero no negativo, opcional). */
  ventas_promedio?: number | null;
  /** Trofeos oficiales: títulos de la edición (`[]` = ninguno). */
  ganador?: string[] | null;
  sedes?: {
    sede_index: number;
    sede_nombre: string;
    sede_direccion?: string;
    horarios_evento: any;
    ubicacion_gps?: string;
    visible_en_ficha?: boolean;
  }[];
}

function rowPropuestaSede(
  propuestaId: string,
  sede: {
    sede_index: number;
    sede_nombre: string;
    sede_direccion?: string;
    horarios_evento: any;
    ubicacion_gps?: string;
    visible_en_ficha?: boolean;
  }
) {
  const gRaw =
    sede.ubicacion_gps !== undefined && sede.ubicacion_gps !== null
      ? String(sede.ubicacion_gps).trim()
      : '';
  const row: Record<string, unknown> = {
    propuesta_id: propuestaId,
    sede_index: sede.sede_index,
    sede_nombre: sede.sede_nombre,
    sede_direccion: sede.sede_direccion,
    horarios_evento: sede.horarios_evento,
    visible_en_ficha: sede.visible_en_ficha !== false,
  };
  // Solo enviar si hay valor: evita error PGRST204 cuando la columna aún no existe en Supabase.
  // Con migración `*_add_ubicacion_gps_to_propuestas_sedes.sql` aplicada, los enlaces GPS sí se guardan.
  if (gRaw) {
    row.ubicacion_gps = gRaw;
  }
  return row;
}

export interface UpdatePropuestaData {
  edicion_id?: number;
  restaurante_id?: string;
  foto_restaurante?: string; // Mantener para compatibilidad
  fotos?: Array<{ url: string; id: string; url_ficha?: string }>;
  foto_seleccionada_index?: number;
  instagram?: string;
  titulo?: string;
  descripcion?: string;
  nota_activa?: boolean;
  nota_titulo?: string | null;
  nota_descripcion?: string | null;
  reel_participante_oficial?: string;
  reel_marcas_historias?: string;
  historias_participante_oficial?: string;
  estado_propuesta?: string;
  estado_publicacion?: string;
  estado_foto?: string;
  estado_titulo?: string;
  estado_descripcion?: string;
  visible_publico?: boolean;
  /** Ventas promedio (entero no negativo, opcional). */
  ventas_promedio?: number | null;
  posicion?: number;
  /** Trofeos oficiales: títulos de la edición (`[]` = ninguno). */
  ganador?: string[] | null;
  sedes?: {
    sede_index: number;
    sede_nombre: string;
    sede_direccion?: string;
    horarios_evento: any;
    ubicacion_gps?: string;
    visible_en_ficha?: boolean;
  }[];
}

function maxFechaModificacionHistorial(prev: string | undefined, next: string): string {
  if (!prev) return next;
  const tp = new Date(prev).getTime();
  const tn = new Date(next).getTime();
  if (Number.isNaN(tn)) return prev;
  if (Number.isNaN(tp)) return next;
  return tn >= tp ? next : prev;
}

export type SeguimientoUltimaModificacion = {
  fecha: string;
  usuarioNombre: string;
};

async function loadSeguimientoHistorialResumen(propuestaIds: string[]): Promise<{
  conRegistroEnHistorial: Set<string>;
  conCambioTituloContenidoEnHistorial: Set<string>;
  conCambioDescripcionContenidoEnHistorial: Set<string>;
  conCambioFotoEnHistorial: Set<string>;
  ultimaModTituloPorPropuesta: Map<string, string>;
  ultimaModDescripcionPorPropuesta: Map<string, string>;
  ultimaModFotoPorPropuesta: Map<string, string>;
  ultimaModificacionPorPropuesta: Map<string, SeguimientoUltimaModificacion>;
}> {
  const ids = [...new Set(propuestaIds.map((id) => String(id).trim()).filter(Boolean))];
  if (ids.length === 0) {
    return {
      conRegistroEnHistorial: new Set(),
      conCambioTituloContenidoEnHistorial: new Set(),
      conCambioDescripcionContenidoEnHistorial: new Set(),
      conCambioFotoEnHistorial: new Set(),
      ultimaModTituloPorPropuesta: new Map(),
      ultimaModDescripcionPorPropuesta: new Map(),
      ultimaModFotoPorPropuesta: new Map(),
      ultimaModificacionPorPropuesta: new Map(),
    };
  }
  const conRegistro = new Set<string>();
  const conCambioTitulo = new Set<string>();
  const conCambioDescripcion = new Set<string>();
  const conCambioFoto = new Set<string>();
  const ultimaModTitulo = new Map<string, string>();
  const ultimaModDescripcion = new Map<string, string>();
  const ultimaModFoto = new Map<string, string>();
  const ultimaModRaw = new Map<string, { fecha: string; usuarioId: string | null }>();
  const chunkSize = 150;
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    const { data, error } = await supabase
      .from('propuestas_historial')
      .select('propuesta_id, campos_modificados, fecha_modificacion, usuario_id')
      .in('propuesta_id', chunk);
    if (error) {
      console.error('loadSeguimientoHistorialResumen:', error);
      throw error;
    }
    for (const row of data ?? []) {
      if (row?.propuesta_id == null) continue;
      const sid = String(row.propuesta_id);
      conRegistro.add(sid);
      const keys = keysCamposModificadosHistorial(row.campos_modificados);
      const fechaRaw = row.fecha_modificacion;
      const fechaValida = fechaRaw != null && String(fechaRaw).trim() !== '';
      const fecha = fechaValida ? String(fechaRaw) : '';
      if (fechaValida) {
        const prev = ultimaModRaw.get(sid);
        const prevTs = prev ? new Date(prev.fecha).getTime() : NaN;
        const nextTs = new Date(fecha).getTime();
        if (!prev || Number.isNaN(prevTs) || (!Number.isNaN(nextTs) && nextTs >= prevTs)) {
          ultimaModRaw.set(sid, {
            fecha,
            usuarioId: row.usuario_id != null ? String(row.usuario_id) : null,
          });
        }
      }
      if (filaHistorialIncluyeCambioTituloContenido(keys)) {
        conCambioTitulo.add(sid);
        if (fechaValida) {
          ultimaModTitulo.set(sid, maxFechaModificacionHistorial(ultimaModTitulo.get(sid), fecha));
        }
      }
      if (filaHistorialIncluyeCambioDescripcionContenido(keys)) {
        conCambioDescripcion.add(sid);
        if (fechaValida) {
          ultimaModDescripcion.set(sid, maxFechaModificacionHistorial(ultimaModDescripcion.get(sid), fecha));
        }
      }
      if (filaHistorialIncluyeCambioFoto(keys)) {
        conCambioFoto.add(sid);
        if (fechaValida) {
          ultimaModFoto.set(sid, maxFechaModificacionHistorial(ultimaModFoto.get(sid), fecha));
        }
      }
    }
  }

  const usuarioIds = [
    ...new Set(
      [...ultimaModRaw.values()]
        .map((v) => v.usuarioId)
        .filter((id): id is string => Boolean(id?.trim()))
    ),
  ];
  const nombrePorUsuario = new Map<string, string>();
  for (let i = 0; i < usuarioIds.length; i += chunkSize) {
    const chunk = usuarioIds.slice(i, i + chunkSize);
    const { data: perfiles } = await supabase
      .from('user_profiles')
      .select('id, nombre, apellido, email')
      .in('id', chunk);
    for (const u of perfiles ?? []) {
      const nombre = [u.nombre, u.apellido].filter(Boolean).join(' ').trim();
      nombrePorUsuario.set(
        String(u.id),
        nombre || (typeof u.email === 'string' && u.email.trim() ? u.email.trim() : '—')
      );
    }
  }

  const ultimaModificacionPorPropuesta = new Map<string, SeguimientoUltimaModificacion>();
  for (const [sid, raw] of ultimaModRaw) {
    ultimaModificacionPorPropuesta.set(sid, {
      fecha: raw.fecha,
      usuarioNombre:
        (raw.usuarioId && nombrePorUsuario.get(raw.usuarioId)) || '—',
    });
  }

  return {
    conRegistroEnHistorial: conRegistro,
    conCambioTituloContenidoEnHistorial: conCambioTitulo,
    conCambioDescripcionContenidoEnHistorial: conCambioDescripcion,
    conCambioFotoEnHistorial: conCambioFoto,
    ultimaModTituloPorPropuesta: ultimaModTitulo,
    ultimaModDescripcionPorPropuesta: ultimaModDescripcion,
    ultimaModFotoPorPropuesta: ultimaModFoto,
    ultimaModificacionPorPropuesta,
  };
}

export const propuestasService = {
  // Obtener todas las propuestas con relaciones
  // Si ciudades_ids está presente, filtra propuestas por restaurantes de esas ciudades (para admins)
  async getAll(ciudades_ids?: string[]): Promise<Propuesta[]> {
    try {
      // Si se proporcionan ciudades_ids, primero obtener los restaurantes de esas ciudades
      let restauranteIds: number[] | undefined;
      if (ciudades_ids && ciudades_ids.length > 0) {
        const ciudadIdsInt = ciudades_ids.map(id => parseInt(id)).filter(id => !isNaN(id));
        if (ciudadIdsInt.length > 0) {
          const { data: restaurantesData, error: restaurantesError } = await supabase
            .from('restaurantes')
            .select('id')
            .in('ciudad_id', ciudadIdsInt);

          if (restaurantesError) {
            console.error('Error obteniendo restaurantes por ciudad:', restaurantesError);
            throw restaurantesError;
          }

          restauranteIds = restaurantesData?.map(r => r.id) || [];
          
          // Si no hay restaurantes en esas ciudades, retornar array vacío
          if (restauranteIds.length === 0) {
            return [];
          }
        }
      }

      let query = supabase
        .from('propuestas')
        .select(`
          *,
          restaurante:restaurantes(${RESTAURANTE_JOIN_SELECT}),
          sedes:propuestas_sedes(*)
        `);

      // Si hay restauranteIds, filtrar por ellos
      if (restauranteIds && restauranteIds.length > 0) {
        query = query.in('restaurante_id', restauranteIds);
      }

      const { data, error } = await query
        .order('posicion', { ascending: true })
        .order('updated_at', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error en getAll:', error);
        throw error;
      }
      
      // Parsear fotos para cada propuesta
      return (data || []).map((propuesta: any) => ({
        ...propuesta,
        fotos: parseJSONSafely(propuesta.fotos, undefined),
        foto_seleccionada_index: propuesta.foto_seleccionada_index !== undefined && propuesta.foto_seleccionada_index !== null ? propuesta.foto_seleccionada_index : undefined,
        ganador: parsePropuestaTrofeos(propuesta.ganador),
      })) as Propuesta[];
    } catch (error) {
      console.error('Error en getAll:', error);
      throw error;
    }
  },

  // Obtener historial completo de una propuesta
  async getHistorial(propuestaId: string): Promise<Array<{
    id: string;
    fecha_modificacion: string;
    tipo: 'creacion' | 'modificacion';
    usuario?: {
      id: string;
      nombre?: string;
      apellido?: string;
      email?: string;
    };
    campos_modificados: string[];
  }>> {
    try {
      console.log('🔍 Obteniendo historial para propuesta:', propuestaId);
      
      // Obtener información de la propuesta (created_by, created_at)
      const { data: propuestaData } = await supabase
        .from('propuestas')
        .select('created_by, created_at, updated_by, updated_at')
        .eq('id', propuestaId)
        .single();

      // Obtener historial de cambios
      const { data: historialData, error } = await supabase
        .from('propuestas_historial')
        .select('*')
        .eq('propuesta_id', propuestaId)
        .order('fecha_modificacion', { ascending: false });

      if (error) {
        console.error('❌ Error obteniendo historial completo:', error);
        console.error('❌ Detalles del error:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
      }

      console.log('✅ Historial obtenido:', historialData?.length || 0, 'registros');

      // Obtener todos los IDs de usuarios necesarios
      const usuarioIds = new Set<string>();
      
      // Agregar created_by si existe
      if (propuestaData?.created_by) {
        usuarioIds.add(propuestaData.created_by);
      }
      
      // Agregar updated_by si existe
      if (propuestaData?.updated_by) {
        usuarioIds.add(propuestaData.updated_by);
      }
      
      // Agregar usuarios del historial
      if (historialData) {
        historialData.forEach(h => {
          if (h.usuario_id) {
            usuarioIds.add(h.usuario_id);
          }
        });
      }

      // Obtener información de todos los usuarios
      const usuariosMap: Record<string, any> = {};
      if (usuarioIds.size > 0) {
        const { data: usuariosData } = await supabase
          .from('user_profiles')
          .select('id, nombre, apellido, email')
          .in('id', Array.from(usuarioIds));

        if (usuariosData) {
          usuariosData.forEach(usuario => {
            usuariosMap[usuario.id] = usuario;
          });
        }
      }

      const historial: Array<{
        id: string;
        fecha_modificacion: string;
        tipo: 'creacion' | 'modificacion';
        usuario?: {
          id: string;
          nombre?: string;
          apellido?: string;
          email?: string;
        };
        campos_modificados: string[];
      }> = [];

      // Agregar información de creación al inicio
      if (propuestaData?.created_at) {
        const creador = propuestaData.created_by ? usuariosMap[propuestaData.created_by] : null;
        historial.push({
          id: `creacion-${propuestaId}`,
          fecha_modificacion: propuestaData.created_at,
          tipo: 'creacion',
          usuario: creador ? {
            id: creador.id,
            nombre: creador.nombre || undefined,
            apellido: creador.apellido || undefined,
            email: creador.email || undefined,
          } : undefined,
          campos_modificados: [],
        });
      }

      // Procesar historial de modificaciones
      if (historialData && historialData.length > 0) {
        historialData.forEach(registro => {
        // Parsear campos modificados
        let camposModificados: string[] = [];
        if (registro.campos_modificados) {
          try {
            if (typeof registro.campos_modificados === 'object' && registro.campos_modificados !== null) {
              camposModificados = Object.keys(registro.campos_modificados);
            } else if (typeof registro.campos_modificados === 'string') {
              const parsed = JSON.parse(registro.campos_modificados);
              camposModificados = Object.keys(parsed);
            }
          } catch (error) {
            console.warn('Error parseando campos_modificados:', error);
          }
        }

        const usuario = registro.usuario_id ? usuariosMap[registro.usuario_id] : null;

          historial.push({
          id: registro.id,
          fecha_modificacion: registro.fecha_modificacion,
            tipo: 'modificacion',
          usuario: usuario ? {
            id: usuario.id,
            nombre: usuario.nombre || undefined,
            apellido: usuario.apellido || undefined,
            email: usuario.email || undefined,
          } : undefined,
          campos_modificados: camposModificados,
          });
        });
      }

      // Ordenar por fecha (más reciente primero)
      return historial.sort((a, b) => {
        const fechaA = new Date(a.fecha_modificacion).getTime();
        const fechaB = new Date(b.fecha_modificacion).getTime();
        return fechaB - fechaA;
      });
    } catch (error) {
      console.error('Error en getHistorial:', error);
      return [];
    }
  },

  /**
   * Resumen para tarjetas de Seguimiento: filas en historial y cambios por campo (claves como en `nombresCampos` al guardar historial).
   */
  async getSeguimientoHistorialResumen(propuestaIds: string[]): Promise<{
    conRegistroEnHistorial: Set<string>;
    conCambioTituloContenidoEnHistorial: Set<string>;
    conCambioDescripcionContenidoEnHistorial: Set<string>;
    conCambioFotoEnHistorial: Set<string>;
    ultimaModTituloPorPropuesta: Map<string, string>;
    ultimaModDescripcionPorPropuesta: Map<string, string>;
    ultimaModFotoPorPropuesta: Map<string, string>;
    ultimaModificacionPorPropuesta: Map<string, SeguimientoUltimaModificacion>;
  }> {
    return loadSeguimientoHistorialResumen(propuestaIds);
  },

  /**
   * Compatibilidad: código o bundles que aún llamen al nombre anterior.
   * Delega en la misma lógica que `getSeguimientoHistorialResumen`.
   */
  async getIdsConRegistrosHistorial(propuestaIds: string[]): Promise<Set<string>> {
    const { conRegistroEnHistorial } = await loadSeguimientoHistorialResumen(propuestaIds);
    return conRegistroEnHistorial;
  },

  // Obtener una propuesta por ID
  async getById(id: string): Promise<Propuesta | null> {
    const { data, error } = await supabase
      .from('propuestas')
      .select(`
        *,
        restaurante:restaurantes(${RESTAURANTE_JOIN_SELECT}),
        sedes:propuestas_sedes(*)
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error en getById:', error);
      return null;
    }

    if (!data) return null;

    const dataConBypass = mergeBypassRangoRestauranteEnJoin(data as Record<string, unknown>);

    // Obtener historial de cambios más reciente
    const { data: historialData, error: historialError } = await supabase
      .from('propuestas_historial')
      .select('*')
      .eq('propuesta_id', id)
      .order('fecha_modificacion', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (historialError) {
      console.warn('Error obteniendo historial:', historialError);
    }

    // Obtener información del usuario del historial si existe
    let usuarioHistorial: any = null;
    if (historialData && historialData.usuario_id) {
      const { data: usuarioData } = await supabase
        .from('user_profiles')
        .select('id, nombre, apellido, email')
        .eq('id', historialData.usuario_id)
        .maybeSingle();
      usuarioHistorial = usuarioData;
    }

    // Obtener información del usuario que modificó (si existe updated_by)
    let usuarioModificador: any = null;
    if (data.updated_by) {
      const { data: usuarioData } = await supabase
        .from('user_profiles')
        .select('id, nombre, apellido, email')
        .eq('id', data.updated_by)
        .maybeSingle();
      usuarioModificador = usuarioData;
    }

    // Parsear fotos si existe
    // Parsear campos_modificados del historial (puede venir como JSONB)
    let camposModificadosArray: string[] | undefined = undefined;
    if (historialData && historialData.campos_modificados) {
      try {
        // Si es un objeto, obtener las claves
        if (typeof historialData.campos_modificados === 'object' && historialData.campos_modificados !== null) {
          camposModificadosArray = Object.keys(historialData.campos_modificados);
        } else if (typeof historialData.campos_modificados === 'string') {
          // Si es un string JSON, parsearlo primero
          const parsed = JSON.parse(historialData.campos_modificados);
          camposModificadosArray = Object.keys(parsed);
        }
        console.log('🔍 Campos modificados detectados:', camposModificadosArray);
      } catch (error) {
        console.warn('Error parseando campos_modificados:', error);
      }
    }

    const restJoin = dataConBypass.restaurante as Record<string, unknown> | null | undefined;
    const restauranteNormalizado =
      restJoin && typeof restJoin === 'object'
        ? {
            ...restJoin,
            // En BD la dirección corta suele ser `direccion`; en app se expone como sede_ubicacion_corta.
            sede_ubicacion_corta:
              (typeof restJoin.sede_ubicacion_corta === 'string' && restJoin.sede_ubicacion_corta.trim()
                ? String(restJoin.sede_ubicacion_corta).trim()
                : undefined) ||
              (typeof restJoin.direccion === 'string' && restJoin.direccion.trim()
                ? String(restJoin.direccion).trim()
                : undefined),
          }
        : restJoin;

    const propuesta = {
      ...dataConBypass,
      restaurante: restauranteNormalizado,
      fotos: parseJSONSafely(data.fotos, undefined),
      foto_seleccionada_index: data.foto_seleccionada_index !== undefined && data.foto_seleccionada_index !== null ? data.foto_seleccionada_index : undefined,
      ganador: parsePropuestaTrofeos(data.ganador),
      ultima_modificacion: historialData ? {
        usuario: usuarioHistorial ? {
          id: usuarioHistorial.id,
          nombre: usuarioHistorial.nombre || undefined,
          apellido: usuarioHistorial.apellido || undefined,
          email: usuarioHistorial.email || undefined,
        } : undefined,
        campos_modificados: camposModificadosArray,
        fecha_modificacion: historialData.fecha_modificacion,
      } : (usuarioModificador ? {
        usuario: {
          id: usuarioModificador.id,
          nombre: usuarioModificador.nombre || undefined,
          apellido: usuarioModificador.apellido || undefined,
          email: usuarioModificador.email || undefined,
        },
        fecha_modificacion: data.updated_at,
      } : undefined),
    };

    return propuesta as Propuesta;
  },

  /**
   * Mapea filas crudas de propuestas (con join restaurante + sedes) al tipo Propuesta.
   */
  _normalizePropuestaRowsFromEdicionQuery(raw: any[]): Propuesta[] {
    return (raw || []).map((propuesta: any) => {
      const merged = mergeBypassRangoRestauranteEnJoin(propuesta as Record<string, unknown>);
      const r = merged.restaurante;
      const restauranteNorm =
        r && typeof r === 'object'
          ? {
              ...r,
              sede_ubicacion_corta:
                (typeof r.sede_ubicacion_corta === 'string' && r.sede_ubicacion_corta.trim()
                  ? r.sede_ubicacion_corta
                  : null) ||
                (typeof r.direccion === 'string' ? r.direccion : undefined),
            }
          : r;
      return {
        ...merged,
        restaurante: restauranteNorm,
        fotos: parseJSONSafely(propuesta.fotos, undefined),
        foto_seleccionada_index:
          propuesta.foto_seleccionada_index !== undefined && propuesta.foto_seleccionada_index !== null
            ? propuesta.foto_seleccionada_index
            : undefined,
        ganador: parsePropuestaTrofeos(propuesta.ganador),
      };
    }) as Propuesta[];
  },

  /**
   * Primera propuesta pública por restaurante (una query), para enlaces desde listados comensal.
   * @param edicionParaEnlace Si la propuesta no tiene `edicion_id`, usar este id en el enlace (p. ej. edición activa).
   */
  async mapPrimeraPropuestaPublicaPorRestauranteIds(
    restauranteIds: string[],
    edicionParaEnlace?: number | null
  ): Promise<Map<string, { propuestaId: string; edicionId: number }>> {
    const map = new Map<string, { propuestaId: string; edicionId: number }>();
    if (restauranteIds.length === 0) return map;
    const nums = [
      ...new Set(
        restauranteIds.map((id) => parseInt(id, 10)).filter((n) => !Number.isNaN(n))
      ),
    ];
    if (nums.length === 0) return map;
    const { data, error } = await supabase
      .from('propuestas')
      .select('id, restaurante_id, edicion_id, posicion')
      .in('restaurante_id', nums)
      .or('visible_publico.is.null,visible_publico.eq.true')
      .order('posicion', { ascending: true })
      .order('updated_at', { ascending: false })
      .order('created_at', { ascending: false });
    if (error || !data) return map;
    const fallbackEd =
      edicionParaEnlace != null && !Number.isNaN(Number(edicionParaEnlace))
        ? Number(edicionParaEnlace)
        : null;
    for (const row of data as Array<{
      id: number | string;
      restaurante_id: number | string;
      edicion_id: number | string | null;
    }>) {
      const rid = String(row.restaurante_id);
      if (map.has(rid)) continue;
      let edicionIdNum: number;
      if (row.edicion_id != null && row.edicion_id !== '') {
        edicionIdNum = Number(row.edicion_id);
        if (Number.isNaN(edicionIdNum)) continue;
      } else if (fallbackEd != null) {
        edicionIdNum = fallbackEd;
      } else {
        continue;
      }
      map.set(rid, {
        propuestaId: String(row.id),
        edicionId: edicionIdNum,
      });
    }
    return map;
  },

  // Obtener propuestas por edición
  // Incluye: (1) propuestas con edicion_id = esta edición; (2) propuestas públicas con edicion_id NULL
  //     cuyo restaurante está inscrito en la edición (restaurantes_ediciones), para no “perder” fichas
  //     si el campo edición no se guardó al crear/editar la propuesta.
  // Solo devuelve visibles al público (visible_publico true o null).
  async getByEdicionId(edicionId: string): Promise<Propuesta[]> {
    const selectClause = `
          *,
          restaurante:restaurantes(${RESTAURANTE_JOIN_SELECT}),
          sedes:propuestas_sedes(*)
        `;
    const visFilter = 'visible_publico.is.null,visible_publico.eq.true';

    try {
      let edNum = Number(edicionId);
      if (Number.isNaN(edNum)) {
        const ed = await resolveEdicionFromRouteParam(edicionId);
        if (ed?.id == null) {
          console.error('getByEdicionId: id de edición inválido', edicionId);
          return [];
        }
        edNum = ed.id;
      }

      const { data: relData, error: relErr } = await supabase
        .from('restaurantes_ediciones')
        .select('restaurante_id')
        .eq('edicion_id', edNum);

      if (relErr) {
        console.warn('getByEdicionId: no se pudieron leer restaurantes de la edición', relErr);
      }

      const ridsInEdicion = [
        ...new Set(
          (relData ?? [])
            .map((r: { restaurante_id?: number | string | null }) => r.restaurante_id)
            .map((raw) => (raw == null || raw === '' ? null : Number(raw)))
            .filter((id): id is number => id != null && !Number.isNaN(id))
        ),
      ];

      const qDirectas = supabase
        .from('propuestas')
        .select(selectClause)
        .eq('edicion_id', edNum)
        .or(visFilter)
        .order('posicion', { ascending: true })
        .order('updated_at', { ascending: false })
        .order('created_at', { ascending: false });

      const qHuérfanas =
        ridsInEdicion.length > 0
          ? supabase
              .from('propuestas')
              .select(selectClause)
              .is('edicion_id', null)
              .in('restaurante_id', ridsInEdicion)
              .or(visFilter)
              .order('posicion', { ascending: true })
              .order('updated_at', { ascending: false })
              .order('created_at', { ascending: false })
          : Promise.resolve({ data: [] as any[], error: null as null });

      const [resA, resB] = await Promise.all([qDirectas, qHuérfanas]);

      if (resA.error) {
        console.error('Error en getByEdicionId (ligadas por edición):', resA.error);
        throw resA.error;
      }
      if (resB.error) {
        console.error('Error en getByEdicionId (sin edición, por restaurante inscrito):', resB.error);
        throw resB.error;
      }

      const byId = new Map<string, any>();
      for (const row of resA.data ?? []) byId.set(String(row.id), row);
      for (const row of resB.data ?? []) {
        const pid = String(row.id);
        if (!byId.has(pid)) byId.set(pid, row);
      }

      const merged = [...byId.values()].sort((a, b) =>
        comparePropuestasPorPosicionYLuegoReciente(a as Propuesta, b as Propuesta)
      );

      return this._normalizePropuestaRowsFromEdicionQuery(merged);
    } catch (error) {
      console.error('Error en getByEdicionId:', error);
      throw error;
    }
  },

  /**
   * Propuesta pública del restaurante en una edición concreta:
   * primero `edicion_id` coincidente; si no hay, propuesta con `edicion_id` null solo si el restaurante
   * está inscrito en esa edición (`restaurantes_ediciones`), misma lógica que en listados por edición.
   */
  async getByRestauranteYEdicion(
    restauranteId: string | number,
    edicionId: string | number
  ): Promise<Propuesta | null> {
    const rid = Number(restauranteId);
    const edNum = Number(edicionId);
    if (Number.isNaN(rid) || Number.isNaN(edNum)) return null;

    const selectClause = `
          *,
          restaurante:restaurantes(${RESTAURANTE_JOIN_SELECT}),
          sedes:propuestas_sedes(*)
        `;
    const visFilter = 'visible_publico.is.null,visible_publico.eq.true';

    const { data: direct, error: errDirect } = await supabase
      .from('propuestas')
      .select(selectClause)
      .eq('restaurante_id', rid)
      .eq('edicion_id', edNum)
      .or(visFilter)
      .order('posicion', { ascending: true })
      .order('updated_at', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (errDirect) {
      console.error('getByRestauranteYEdicion (directa):', errDirect);
      return null;
    }
    if (direct) {
      const arr = this._normalizePropuestaRowsFromEdicionQuery([direct]);
      return arr[0] ?? null;
    }

    const { data: link } = await supabase
      .from('restaurantes_ediciones')
      .select('id')
      .eq('restaurante_id', rid)
      .eq('edicion_id', edNum)
      .maybeSingle();

    if (!link) return null;

    const { data: orphan, error: errOrphan } = await supabase
      .from('propuestas')
      .select(selectClause)
      .eq('restaurante_id', rid)
      .is('edicion_id', null)
      .or(visFilter)
      .order('posicion', { ascending: true })
      .order('updated_at', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (errOrphan) {
      console.error('getByRestauranteYEdicion (huérfana):', errOrphan);
      return null;
    }
    if (!orphan) return null;
    const arr2 = this._normalizePropuestaRowsFromEdicionQuery([orphan]);
    return arr2[0] ?? null;
  },

  /** Asigna `posicion` a varias propuestas (reordenar, sin historial). */
  async actualizarPosiciones(items: Array<{ id: string; posicion: number }>): Promise<void> {
    if (items.length === 0) return;
    const results = await Promise.all(
      items.map(({ id, posicion }) =>
        supabase.from('propuestas').update({ posicion }).eq('id', id)
      )
    );
    const failed = results.find((r) => r.error);
    if (failed?.error) throw failed.error;
  },

  // Crear una nueva propuesta
  async create(propuestaData: CreatePropuestaData): Promise<Propuesta> {
    const { sedes, fotos, foto_seleccionada_index, ...propuestaInfo } = propuestaData;

    // Obtener usuario actual
    const { data: { user } } = await supabase.auth.getUser();
    const usuarioId = user?.id;

    // Preparar datos para insertar (JSONB: enviar array, no string)
    const datosParaInsertar: any = {
      ...propuestaInfo,
      fotos: Array.isArray(fotos) && fotos.length > 0 ? fotos : null,
      foto_seleccionada_index: foto_seleccionada_index !== undefined && foto_seleccionada_index !== null ? foto_seleccionada_index : null,
      ganador: parsePropuestaTrofeos(propuestaInfo.ganador),
      ventas_promedio: parseVentasPromedioEntero(propuestaInfo.ventas_promedio),
    };

    let qMax = supabase.from('propuestas').select('posicion');
    if (datosParaInsertar.edicion_id != null && datosParaInsertar.edicion_id !== '') {
      qMax = qMax.eq('edicion_id', datosParaInsertar.edicion_id);
    } else {
      qMax = qMax.is('edicion_id', null);
    }
    const { data: maxPosRow, error: maxPosErr } = await qMax
      .order('posicion', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (maxPosErr) {
      console.warn('create propuesta: no se pudo leer posición máxima', maxPosErr);
    }
    const maxP = maxPosRow?.posicion != null ? Number(maxPosRow.posicion) : 0;
    datosParaInsertar.posicion = maxP + 10;

    // Si hay usuario, establecer created_by
    if (usuarioId) {
      datosParaInsertar.created_by = usuarioId;
    }

    // Crear la propuesta
    const { data: propuesta, error: propuestaError } = await supabase
      .from('propuestas')
      .insert(datosParaInsertar)
      .select()
      .single();

    if (propuestaError) throw propuestaError;

    // Crear las sedes si se proporcionan (incl. ubicacion_gps; migración 20250116000000)
    if (sedes && sedes.length > 0) {
      const sedesData = sedes.map((sede) => rowPropuestaSede(propuesta.id, sede));

      const { error: sedesError } = await supabase
        .from('propuestas_sedes')
        .insert(sedesData);

      if (sedesError) throw sedesError;
    }

    // Retornar la propuesta creada con sus relaciones
    return this.getById(propuesta.id) as Promise<Propuesta>;
  },

  // Actualizar una propuesta
  async update(id: string, propuestaData: UpdatePropuestaData): Promise<Propuesta> {
    const {
      sedes,
      fotos,
      foto_seleccionada_index,
      visible_publico,
      ganador,
      ...propuestaInfo
    } = propuestaData;

    // Obtener la propuesta anterior para comparar cambios (solo campos necesarios)
    const { data: propuestaAnterior, error: errorAnterior } = await supabase
      .from('propuestas')
      .select('titulo, descripcion, restaurante_id, edicion_id, instagram, visible_publico, fotos, foto_seleccionada_index, estado_foto, estado_titulo, estado_descripcion, posicion, ganador, nota_activa, nota_titulo, nota_descripcion, ventas_promedio')
      .eq('id', id)
      .single();

    if (errorAnterior || !propuestaAnterior) {
      throw new Error('Propuesta no encontrada');
    }

    // Obtener sedes anteriores para detectar cambios en horarios/sedes
    let sedesAnteriores: Array<{
      sede_index: number;
      sede_nombre: string;
      sede_direccion?: string;
      horarios_evento: any;
      visible_en_ficha?: boolean | null;
    }> = [];
    if (sedes !== undefined) {
      const { data: sedesData } = await supabase
        .from('propuestas_sedes')
        .select('sede_index, sede_nombre, sede_direccion, horarios_evento, visible_en_ficha')
        .eq('propuesta_id', id)
        .order('sede_index', { ascending: true });
      sedesAnteriores = sedesData || [];
    }

    // Obtener usuario actual
    const { data: { user } } = await supabase.auth.getUser();
    const usuarioId = user?.id;

    // Preparar datos para actualizar (JSONB: enviar array, no string)
    const datosParaActualizar: any = {
      ...propuestaInfo,
    };
    for (const key of Object.keys(datosParaActualizar)) {
      if (datosParaActualizar[key] === undefined) {
        delete datosParaActualizar[key];
      }
    }

    if (datosParaActualizar.restaurante_id != null && datosParaActualizar.restaurante_id !== '') {
      const rid = Number(datosParaActualizar.restaurante_id);
      if (Number.isFinite(rid)) datosParaActualizar.restaurante_id = rid;
    }

    if (datosParaActualizar.ventas_promedio !== undefined) {
      datosParaActualizar.ventas_promedio = parseVentasPromedioEntero(datosParaActualizar.ventas_promedio);
    }

    // Si fotos está definido (incluso si es array vacío), actualizarlo
    // Esto permite eliminar todas las fotos enviando un array vacío
    if (fotos !== undefined) {
      datosParaActualizar.fotos = Array.isArray(fotos) && fotos.length > 0 ? fotos : null;
    }
    
    // Si foto_seleccionada_index está definido, actualizarlo
    if (foto_seleccionada_index !== undefined) {
      datosParaActualizar.foto_seleccionada_index = foto_seleccionada_index !== null && foto_seleccionada_index !== undefined ? foto_seleccionada_index : null;
    }
    
    // Si visible_publico está definido, actualizarlo
    if (visible_publico !== undefined) {
      datosParaActualizar.visible_publico = visible_publico;
      console.log('📝 Actualizando visible_publico a:', visible_publico);
    }

    if (ganador !== undefined) {
      datosParaActualizar.ganador = parsePropuestaTrofeos(ganador);
    }

    // Detectar campos modificados
    const camposModificados: Record<string, any> = {};
    const nombresCampos: Record<string, string> = {
      titulo: 'Título',
      descripcion: 'Descripción',
      restaurante_id: 'Restaurante',
      edicion_id: 'Edición',
      instagram: 'Instagram',
      visible_publico: 'Visibilidad pública',
      fotos: 'Fotos',
      foto_seleccionada_index: 'Foto seleccionada',
      reel_participante_oficial: 'Reel participante oficial',
      reel_marcas_historias: 'Reel marcas/historias',
      historias_participante_oficial: 'Historias participante oficial',
      estado_foto: 'Estado foto',
      estado_titulo: 'Estado título',
      estado_descripcion: 'Estado descripción',
      posicion: 'Posición',
      ganador: 'Trofeos',
      nota_activa: 'Nota activa',
      nota_titulo: 'Título de nota',
      nota_descripcion: 'Descripción de nota',
      ventas_promedio: 'Ventas promedio',
    };

    // Comparar campos que están en datosParaActualizar
    for (const [campo, valorNuevo] of Object.entries(datosParaActualizar)) {
      // Saltar campos que no queremos rastrear
      if (campo === 'updated_at' || campo === 'created_at' || campo === 'updated_by' || campo === 'created_by') {
        continue;
      }

      const valorAnterior = propuestaAnterior[campo as keyof typeof propuestaAnterior];
      
      // Comparar valores (considerando null/undefined y tipos especiales)
      let sonDiferentes = false;
      
      if (campo === 'fotos' || campo === 'ganador') {
        try {
          const ant =
            campo === 'ganador'
              ? parsePropuestaTrofeos(valorAnterior)
              : typeof valorAnterior === 'string'
                ? JSON.parse((valorAnterior as string) || '[]')
                : valorAnterior || [];
          const neu =
            campo === 'ganador'
              ? parsePropuestaTrofeos(valorNuevo)
              : typeof valorNuevo === 'string'
                ? JSON.parse(valorNuevo || '[]')
                : Array.isArray(valorNuevo)
                  ? valorNuevo
                  : [];
          sonDiferentes = JSON.stringify(ant) !== JSON.stringify(neu);
        } catch (e) {
          sonDiferentes = String(valorAnterior || '') !== String(valorNuevo || '');
        }
      } else {
        // Para otros campos, comparar como strings
        const valorAnteriorNormalizado = valorAnterior === null || valorAnterior === undefined ? null : String(valorAnterior);
        const valorNuevoNormalizado = valorNuevo === null || valorNuevo === undefined ? null : String(valorNuevo);
        sonDiferentes = valorAnteriorNormalizado !== valorNuevoNormalizado;
      }
      
      if (sonDiferentes) {
        const nombreCampo = nombresCampos[campo] || campo;
        camposModificados[nombreCampo] = valorAnterior;
        console.log(`📝 Campo modificado detectado: ${nombreCampo} (${campo})`);
      }
    }

    // Comparar sedes (horarios y datos) para registrar en historial
    if (sedes !== undefined && sedesAnteriores.length >= 0) {
      const normalizarSedes = (
        arr: Array<{
          sede_index?: number;
          sede_nombre?: string;
          sede_direccion?: string;
          horarios_evento?: any;
          visible_en_ficha?: boolean | null;
        }>
      ) =>
        JSON.stringify(
          arr
            .map((s) => ({
              i: s.sede_index,
              n: s.sede_nombre || '',
              d: s.sede_direccion || '',
              h: s.horarios_evento == null ? '' : String(s.horarios_evento),
              v: s.visible_en_ficha !== false,
            }))
            .sort((a, b) => (a.i ?? 0) - (b.i ?? 0))
        );
      const anterior = normalizarSedes(sedesAnteriores);
      const nuevo = normalizarSedes(sedes);
      if (anterior !== nuevo) {
        camposModificados['Horarios de sedes'] = anterior;
        console.log('📝 Campo modificado detectado: Horarios de sedes');
      }
    }

    // Si hay campos modificados, guardar en historial
    if (Object.keys(camposModificados).length > 0 && usuarioId) {
      console.log('📝 Guardando historial con campos modificados:', Object.keys(camposModificados));
      const { error: historialError } = await supabase
        .from('propuestas_historial')
        .insert({
          propuesta_id: id,
          usuario_id: usuarioId,
          campos_modificados: camposModificados,
        });
      
      if (historialError) {
        console.error('❌ Error guardando historial:', historialError);
      } else {
        console.log('✅ Historial guardado correctamente');
      }
    } else {
      console.log('⚠️ No se guardó historial. Campos modificados:', Object.keys(camposModificados).length, 'Usuario ID:', usuarioId);
    }

    // Actualizar y exigir fila devuelta: un UPDATE con 0 filas (RLS/id) no lanza error en PostgREST.
    const { data: propuestaActualizada, error: propuestaError } = await supabase
      .from('propuestas')
      .update(datosParaActualizar)
      .eq('id', id)
      .select('id, titulo, descripcion, fotos, foto_seleccionada_index')
      .maybeSingle();

    if (propuestaError) throw propuestaError;
    if (!propuestaActualizada) {
      throw new Error(
        'No se aplicó el cambio en la base de datos (0 filas). Recarga la página e inténtalo de nuevo.'
      );
    }

    // Actualizar las sedes si se proporcionan
    if (sedes !== undefined) {
      // Eliminar sedes existentes
      const { error: deleteError } = await supabase
        .from('propuestas_sedes')
        .delete()
        .eq('propuesta_id', id);

      if (deleteError) throw deleteError;

      // Crear nuevas sedes si se proporcionan (preservar ubicacion_gps al reemplazar filas)
      if (sedes.length > 0) {
        const sedesData = sedes.map((sede) => rowPropuestaSede(id, sede));

        const { error: sedesError } = await supabase
          .from('propuestas_sedes')
          .insert(sedesData);

        if (sedesError) throw sedesError;
      }
    }

    // Retornar la propuesta actualizada con sus relaciones
    return this.getById(id) as Promise<Propuesta>;
  },

  // Eliminar una propuesta
  async delete(id: string): Promise<void> {
    // Verificar que la propuesta existe antes de eliminar
    const { data: propuestaExistente, error: errorVerificacion } = await supabase
      .from('propuestas')
      .select('id, titulo')
      .eq('id', id)
      .single();
    
    if (errorVerificacion) {
      throw errorVerificacion;
    }
    
    if (!propuestaExistente) {
      return; // La propuesta ya no existe
    }
    
    // Primero eliminar las sedes relacionadas
    const { error: sedesError } = await supabase
      .from('propuestas_sedes')
      .delete()
      .eq('propuesta_id', id);

    if (sedesError) {
      throw sedesError;
    }

    // Luego eliminar la propuesta
    const { error } = await supabase
      .from('propuestas')
      .delete()
      .eq('id', id);

    if (error) {
      throw error;
    }
  },

  // Obtener restaurantes para el dropdown
  async getRestaurantes(): Promise<{
    id: string;
    nombre: string;
    ciudad_id?: string;
    ciudad_nombre?: string;
    ediciones_ids?: number[];
    logo?: string | null;
  }[]> {
    const { data, error } = await supabase
      .from('restaurantes')
      .select(`
        id, 
        nombre_mostrar,
        ciudad_id,
        imagen_principal,
        ciudades(nombre),
        restaurantes_ediciones(edicion_id)
      `)
      .order('nombre_mostrar');

    if (error) throw error;
    
    return data?.map(item => {
      const ediciones_ids = (item.restaurantes_ediciones as any[])?.map((re: any) => re.edicion_id) || [];
      const logo = item.imagen_principal && String(item.imagen_principal).trim()
        ? String(item.imagen_principal).trim()
        : null;
      const ciudadJoin = (item as { ciudades?: { nombre?: string } | { nombre?: string }[] | null }).ciudades;
      const ciudad_nombre = Array.isArray(ciudadJoin)
        ? ciudadJoin[0]?.nombre
        : ciudadJoin?.nombre;
      return {
        id: item.id.toString(),
        nombre: item.nombre_mostrar,
        ciudad_id: item.ciudad_id?.toString(),
        ciudad_nombre: ciudad_nombre?.trim() || undefined,
        ediciones_ids,
        logo,
      };
    }) || [];
  },

  // Obtener sedes de un restaurante
  async getSedesByRestaurante(restauranteId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('restaurantes')
      .select('sedes')
      .eq('id', restauranteId)
      .single();

    if (error) throw error;
    
    // Parse the JSON sedes array and add index for each sede
    const sedesArray = data?.sedes ? JSON.parse(data.sedes) : [];
    return sedesArray.map((sede: any, index: number) => ({
      ...sede,
      id: index, // Use index as ID for consistency
      sede_index: index
    }));
  },

  // Obtener imagen principal de un restaurante
  async getImagenPrincipalByRestaurante(restauranteId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('restaurantes')
      .select('imagen_principal')
      .eq('id', restauranteId)
      .single();

    if (error) throw error;
    return data?.imagen_principal || null;
  },

  async getRestauranteInfo(restauranteId: string): Promise<{ ciudad: string; sedes: any[]; sede_horario: string; sede_ubicacion_corta?: string; direccion_completa?: string }> {
    const { data, error } = await supabase
      .from('restaurantes')
      .select(`
        ciudad:ciudades(nombre),
        sedes,
        sede_horario:dias_abierto,
        direccion,
        direccion_completa
      `)
      // Forzar a número por si el id en Supabase es INTEGER
      .eq('id', Number(restauranteId))
      .single();

    if (error) throw error;

    // Asegurar que sedes sea siempre un array (parseando si viene como string JSON)
    let sedes: any[] = [];
    const rawSedes = (data as any)?.sedes;
    if (rawSedes) {
      if (typeof rawSedes === 'string') {
        try {
          sedes = JSON.parse(rawSedes);
        } catch (e) {
          console.error('Error parseando sedes de restaurante:', e);
          sedes = [];
        }
      } else if (Array.isArray(rawSedes)) {
        sedes = rawSedes;
      }
    }

    return {
      ciudad: data?.ciudad?.nombre || 'Sin ciudad',
      sedes,
      sede_horario: data?.sede_horario || '',
      sede_ubicacion_corta: data?.direccion || null,
      direccion_completa: data?.direccion_completa || null,
    };
  },

  /**
   * Propuestas con al menos un trofeo etiquetado (`ganador` jsonb array no vacío).
   * Consulta ligera: no calcula ranking desde votos.
   */
  async getGanadoresEtiquetadosByEdicionIds(
    edicionIds: Array<number | string>
  ): Promise<
    Array<{
      propuesta_id: string;
      titulo: string;
      restaurante: string;
      edicion_id: number;
      ganador: string[];
      foto_url: string | null;
      logo_url: string | null;
      ciudad_id: string | null;
    }>
  > {
    const ids = [
      ...new Set(
        edicionIds
          .map((id) => Number(id))
          .filter((n) => Number.isFinite(n) && n > 0)
      ),
    ];
    if (ids.length === 0) return [];

    const { data, error } = await supabase
      .from('propuestas')
      .select(
        `
        id,
        titulo,
        edicion_id,
        ganador,
        foto_restaurante,
        fotos,
        foto_seleccionada_index,
        restaurante:restaurantes(
          id,
          nombre_mostrar,
          nombre_oficial,
          ciudad_id,
          imagen_principal,
          logos,
          logo_seleccionado_index
        )
      `
      )
      .in('edicion_id', ids)
      .not('ganador', 'eq', '[]')
      .or('visible_publico.is.null,visible_publico.eq.true');

    if (error) {
      console.error('getGanadoresEtiquetadosByEdicionIds:', error);
      throw error;
    }

    const resolveFoto = (row: any): string | null => {
      let fotos: Array<{ url?: string; url_ficha?: string }> = [];
      if (typeof row.fotos === 'string' && row.fotos.trim()) {
        try {
          const parsed = JSON.parse(row.fotos);
          if (Array.isArray(parsed)) fotos = parsed;
        } catch {
          /* ignore */
        }
      } else if (Array.isArray(row.fotos)) {
        fotos = row.fotos;
      }
      if (fotos.length > 0) {
        const idx =
          typeof row.foto_seleccionada_index === 'number' &&
          row.foto_seleccionada_index >= 0 &&
          row.foto_seleccionada_index < fotos.length
            ? row.foto_seleccionada_index
            : 0;
        const item = fotos[idx] || fotos[0];
        const url = (item?.url_ficha || item?.url || '').trim();
        if (url) return url;
      }
      if (row.foto_restaurante && String(row.foto_restaurante).trim()) {
        return String(row.foto_restaurante).trim();
      }
      return null;
    };

    const resolveLogo = (rest: any): string | null => {
      if (!rest) return null;
      let logos: Array<{ url?: string }> = [];
      if (typeof rest.logos === 'string' && rest.logos.trim()) {
        try {
          const parsed = JSON.parse(rest.logos);
          if (Array.isArray(parsed)) logos = parsed;
        } catch {
          /* ignore */
        }
      } else if (Array.isArray(rest.logos)) {
        logos = rest.logos;
      }
      if (logos.length > 0) {
        const rawIdx = rest.logo_seleccionado_index;
        const idx =
          typeof rawIdx === 'number' && rawIdx >= 0 && rawIdx < logos.length
            ? rawIdx
            : Number.isFinite(Number(rawIdx)) &&
                Number(rawIdx) >= 0 &&
                Number(rawIdx) < logos.length
              ? Number(rawIdx)
              : 0;
        const url = (logos[idx]?.url || logos[0]?.url || '').trim();
        if (url) return url;
      }
      const img = rest.imagen_principal;
      return img && String(img).trim() ? String(img).trim() : null;
    };

    return (data || [])
      .map((row: any) => {
        const trofeos = parsePropuestaTrofeos(row.ganador);
        if (trofeos.length === 0) return null;
        const edicionId = Number(row.edicion_id);
        if (!Number.isFinite(edicionId)) return null;
        return {
          propuesta_id: String(row.id),
          titulo: String(row.titulo || 'Propuesta'),
          restaurante:
            row.restaurante?.nombre_mostrar ||
            row.restaurante?.nombre_oficial ||
            'Restaurante',
          edicion_id: edicionId,
          ganador: trofeos,
          foto_url: resolveFoto(row),
          logo_url: resolveLogo(row.restaurante),
          ciudad_id:
            row.restaurante?.ciudad_id != null
              ? String(row.restaurante.ciudad_id)
              : null,
        };
      })
      .filter(Boolean) as Array<{
      propuesta_id: string;
      titulo: string;
      restaurante: string;
      edicion_id: number;
      ganador: string[];
      foto_url: string | null;
      logo_url: string | null;
      ciudad_id: string | null;
    }>;
  },
};
